import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

interface Employee {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  personal_rate?: number;
  is_active: boolean;
  is_admin: boolean;
}

const EmployeesManagement: React.FC = () => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    personalRate: '',
    isActive: true,
    isAdmin: false,
  });

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      const res = await api.get('/employees');
      setEmployees(res.data);
    } catch (err) {
      console.error('Failed to load employees:', err);
    }
  };

  const handleOpenDialog = (employee?: Employee) => {
    if (employee) {
      setEditingEmployee(employee);
      setFormData({
        fullName: employee.full_name,
        email: employee.email,
        phone: employee.phone,
        password: '',
        personalRate: employee.personal_rate?.toString() || '',
        isActive: employee.is_active,
        isAdmin: employee.is_admin,
      });
    } else {
      setEditingEmployee(null);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        password: '',
        personalRate: '',
        isActive: true,
        isAdmin: false,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEmployee(null);
  };

  const handleSubmit = async () => {
    try {
      const data: any = {
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        personalRate: formData.personalRate ? parseFloat(formData.personalRate) : null,
        isActive: formData.isActive,
        isAdmin: formData.isAdmin,
      };
      if (formData.password) {
        data.password = formData.password;
      }

      if (editingEmployee) {
        await api.put(`/employees/${editingEmployee.id}`, data);
      } else {
        await api.post('/employees', { ...data, password: formData.password || 'default123' });
      }
      handleCloseDialog();
      loadEmployees();
    } catch (err) {
      console.error('Failed to save employee:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('admin.deleteEmployeeConfirm'))) return;
    try {
      await api.delete(`/employees/${id}`);
      loadEmployees();
    } catch (err) {
      console.error('Failed to delete employee:', err);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('admin.addEmployee')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('profile.fullName')}</TableCell>
              <TableCell>{t('profile.email')}</TableCell>
              <TableCell>{t('profile.phone')}</TableCell>
              <TableCell>{t('reports.rate')}</TableCell>
              <TableCell>{t('admin.isActive')}</TableCell>
              <TableCell>{t('admin.isAdmin')}</TableCell>
              <TableCell>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map((employee) => (
              <TableRow key={employee.id}>
                <TableCell>{employee.full_name}</TableCell>
                <TableCell>{employee.email}</TableCell>
                <TableCell>{employee.phone}</TableCell>
                <TableCell>{employee.personal_rate || '-'}</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {employee.is_active ? (
                    <Box
                      component="span"
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: 'success.light',
                        color: 'success.contrastText',
                        fontSize: '0.75rem',
                      }}
                    >
                      {t('admin.isActive')}
                    </Box>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: 'error.light',
                        color: 'error.contrastText',
                        fontSize: '0.75rem',
                      }}
                    >
                      {t('admin.inactive')}
                    </Box>
                  )}
                </Box>
              </TableCell>
              <TableCell>{employee.is_admin ? t('admin.yes') : t('admin.no')}</TableCell>
              <TableCell>
                <IconButton size="small" onClick={() => handleOpenDialog(employee)} title="Edit">
                  <EditIcon />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={() => handleDelete(employee.id)} 
                  title="Delete"
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingEmployee ? t('admin.editEmployee') : t('admin.addEmployee')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('profile.fullName')}
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t('profile.email')}
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t('profile.phone')}
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t('auth.password')}
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            sx={{ mt: 2 }}
            helperText={editingEmployee ? t('admin.passwordLeaveEmpty') : t('admin.passwordRequired')}
          />
          <TextField
            fullWidth
            label={t('admin.personalRate')}
            type="number"
            value={formData.personalRate}
            onChange={(e) => setFormData({ ...formData, personalRate: e.target.value })}
            sx={{ mt: 2 }}
          />
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={t('admin.activeUserCanLogin')}
            />
            <FormHelperText>{t('admin.inactiveUsersCannotLogin')}</FormHelperText>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isAdmin}
                onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })}
              />
            }
            label={t('admin.isAdmin')}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeesManagement;

